import {
  EmptyState,
  FOCUS_RING,
  ProgressLinear,
  cn,
  useLanguage,
  useTranslate,
  CARD_INTERACTIVE,
  CARD_SURFACE,
} from '@athena/ui'
import { Link } from '@tanstack/react-router'

import { useFormat } from '../../lib/format.js'

export interface AttemptFeedRow {
  attemptId: string
  studentId: string
  studentName: string
  practiceTitle: string
  correctCount: number
  questionCount: number
  percent: number
  /** Whether the tutor's plan opens this attempt answer by answer. */
  planIncludesReview: boolean
  submittedAt: string
}

export interface AttemptFeedProps {
  attempts: readonly AttemptFeedRow[]
  emptyTitle: string
  emptyDescription: string
}

/**
 * Recent attempts as cards rather than table rows (handoff, Organisms >
 * Dashboard summary).
 *
 * Each card names the student first, because a tutor scanning this list is
 * looking for a person before a practice. The bar is the one place the shape
 * of the score appears, and its label carries the figures, so the bar is never
 * the only way to read the result.
 *
 * The card opens the attempt itself when the tutor's plan does: every row
 * here is submitted, so there is always a score, and the answers behind it
 * open for the latest attempt at a practice on every plan.
 */
export function AttemptFeed({ attempts, emptyTitle, emptyDescription }: AttemptFeedProps) {
  const t = useTranslate()
  const language = useLanguage()
  const format = useFormat()

  if (attempts.length === 0) {
    return <EmptyState variant="section" title={emptyTitle} description={emptyDescription} />
  }

  return (
    <ul className="grid gap-4 md:grid-cols-2 desktop:grid-cols-3">
      {attempts.map((attempt) => {
        const content = (
          <>
            <span className="flex flex-col gap-0.5">
              <span className="text-h4 font-semibold text-text wrap-anywhere">
                {attempt.studentName}
              </span>
              <span className="text-body-sm text-text-secondary wrap-anywhere">
                {attempt.practiceTitle}
              </span>
            </span>

            <ProgressLinear
              value={attempt.percent}
              label={t('dashboard.feedProgressLabel', [
                attempt.percent,
                attempt.correctCount,
                attempt.questionCount,
                format.dateTime(attempt.submittedAt),
              ])}
            />
          </>
        )
        return (
          <li key={attempt.attemptId}>
            {/* The score is always the tutor's to read. The card opens the
                answers only when the plan does — an earlier attempt on Free
                is a score, not a link that would refuse. */}
            {attempt.planIncludesReview ? (
              <Link
                to="/$language/attempts/$attemptId"
                params={{ language, attemptId: attempt.attemptId }}
                className={cn(
                  CARD_SURFACE,
                  CARD_INTERACTIVE,
                  'flex h-full cursor-pointer flex-col gap-3 p-6',
                  FOCUS_RING,
                )}
              >
                {content}
              </Link>
            ) : (
              <div className={cn(CARD_SURFACE, 'flex h-full flex-col gap-3 p-6')}>{content}</div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
